import { useState } from 'react';
import api from '../../lib/api';
import useIsMobile from '../../lib/useIsMobile';
import MarketingNav from './MarketingNav';
import Footer from './Footer';

const C = {
  bg: '#FAFBFC',
  white: '#FFFFFF',
  navy: '#1B2B4B',
  accent: '#2563EB',
  green: '#16A34A',
  muted: '#64748B',
  light: '#94A3B8',
  border: '#E2E8F0',
};

const inputStyle = {
  width: '100%',
  padding: '12px 14px',
  background: C.white,
  border: `1.5px solid ${C.border}`,
  borderRadius: 8,
  color: C.navy,
  fontSize: 14,
  outline: 'none',
  boxSizing: 'border-box',
  fontFamily: 'inherit',
  transition: 'border-color 0.2s',
};

const labelStyle = {
  display: 'block',
  fontSize: 13,
  fontWeight: 600,
  color: C.muted,
  marginBottom: 6,
};

const benefits = [
  'Your clients save $30,000+ on their income taxes',
  'Studies start at $799 — backed by 1,200+ completed studies',
  'PE-certified, IRS audit-ready, delivered in 2 weeks',
  'You get your own co-branded landing page with your logo',
  'One referral link — tracking is automatic, no codes needed',
  '$100 per completed study, no cap',
];

const LICENSE_TYPES = [
  { value: '', label: 'Select your profession...' },
  { value: 'CPA', label: 'CPA' },
  { value: 'EA', label: 'EA (Enrolled Agent)' },
  { value: 'Tax Attorney', label: 'Tax Attorney' },
  { value: 'Attorney', label: 'Attorney / Lawyer' },
  { value: 'Real Estate Agent', label: 'Real Estate Agent / Broker' },
  { value: 'Title Company', label: 'Title Company' },
  { value: 'Mortgage Broker', label: 'Mortgage Broker / Lender' },
  { value: 'Financial Advisor', label: 'Financial Advisor' },
  { value: 'Property Manager', label: 'Property Manager' },
  { value: 'Insurance Agent', label: 'Insurance Agent' },
  { value: 'Other', label: 'Other' },
];

export default function AffiliatePage() {
  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    firmName: '',
    licenseType: '',
    licenseNumber: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(null);
  const [logoFile, setLogoFile] = useState(null);
  const [logoUploading, setLogoUploading] = useState(false);
  const [logoUploaded, setLogoUploaded] = useState(false);
  const isMobile = useIsMobile();

  const s = (field) => (e) => setForm((p) => ({ ...p, [field]: e.target.value }));

  const formatPhone = (val) => {
    const digits = val.replace(/\D/g, '').slice(0, 10);
    if (digits.length === 0) return '';
    if (digits.length <= 3) return `(${digits}`;
    if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');
    try {
      const res = await api.post('/affiliates/signup', form);
      setSuccess(res.data);
      if (logoFile && res.data.id) {
        setLogoUploading(true);
        try {
          const fd = new FormData();
          fd.append('logo', logoFile);
          await api.post(`/affiliates/upload-logo/${res.data.id}`, fd, {
            headers: { 'Content-Type': 'multipart/form-data' },
          });
          setLogoUploaded(true);
        } catch {
        } finally {
          setLogoUploading(false);
        }
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={{ minHeight: '100vh', background: C.bg }}>
      <MarketingNav />

      <section style={{ padding: isMobile ? '60px 20px 0' : '80px 24px 0', textAlign: 'center' }}>
        <div style={{ maxWidth: 800, margin: '0 auto' }}>
          <h1 style={{
            fontSize: isMobile ? 28 : 44, fontWeight: 900, color: C.navy,
            margin: '0 0 16px', lineHeight: 1.15, letterSpacing: '-0.02em',
          }}>
            Give your clients a <span style={{ color: C.green }}>$30,000 tax win</span>. Earn $100 per study.
          </h1>
          <p style={{
            fontSize: isMobile ? 15 : 18, color: C.muted, margin: '0 0 48px',
            lineHeight: 1.6, maxWidth: 600, marginLeft: 'auto', marginRight: 'auto',
          }}>
            You make the introduction. We do the work. Your clients save $30,000+ on their income taxes. You earn $100 per study.
          </p>
        </div>
      </section>

      <section style={{ padding: isMobile ? '0 20px 60px' : '0 24px 100px' }}>
        <div style={{
          maxWidth: 1100, margin: '0 auto',
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
          gap: isMobile ? 36 : 60,
          alignItems: 'start',
        }}>
          <div style={{ paddingTop: isMobile ? 0 : 20 }}>
            <h2 style={{ fontSize: isMobile ? 22 : 28, fontWeight: 700, color: C.navy, margin: '0 0 12px' }}>
              Here's how it works
            </h2>
            <p style={{ fontSize: isMobile ? 14 : 15, color: C.muted, lineHeight: 1.7, margin: '0 0 36px' }}>
              Recommend ClearSeg to clients with rental properties. We handle the entire study. They save on taxes. You earn $100 per completed study. Zero work on your end.
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
              {benefits.map((b, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 14 }}>
                  <div style={{
                    width: 28, height: 28, borderRadius: 7, flexShrink: 0,
                    background: '#F0FDF4', border: '1px solid #BBF7D0',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    color: C.green, fontSize: 14, fontWeight: 700,
                  }}>
                    &#10003;
                  </div>
                  <span style={{ fontSize: isMobile ? 15 : 16, color: C.navy, fontWeight: 500, lineHeight: 1.6 }}>{b}</span>
                </div>
              ))}
            </div>
          </div>

          <div style={{
            background: C.white, borderRadius: 16,
            border: `1px solid ${C.border}`, padding: isMobile ? '28px 20px' : '36px 32px',
          }}>
            {success ? (
              <div>
                <div style={{
                  background: '#F0FDF4', border: '1.5px solid #BBF7D0',
                  borderRadius: 12, padding: '28px 24px', textAlign: 'center', marginBottom: 20,
                }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: C.green, marginBottom: 8 }}>
                    Your Referral Code
                  </div>
                  <div style={{
                    fontSize: isMobile ? 24 : 32, fontWeight: 800, color: C.navy,
                    letterSpacing: 2, fontFamily: 'monospace',
                  }}>
                    {success.referralCode}
                  </div>
                </div>
                <p style={{ fontSize: 14, color: C.muted, lineHeight: 1.7, textAlign: 'center', margin: '0 0 16px' }}>
                  Your application is pending approval. You'll receive an email once approved.
                </p>

                {logoUploading && (
                  <p style={{ fontSize: 13, color: C.accent, textAlign: 'center', margin: '0 0 12px' }}>
                    Uploading logo...
                  </p>
                )}
                {logoUploaded && (
                  <div style={{
                    background: `${C.accent}08`, border: `1px solid ${C.accent}22`,
                    borderRadius: 8, padding: '12px 16px', textAlign: 'center',
                    fontSize: 13, color: C.accent, marginBottom: 12,
                  }}>
                    Logo uploaded! Your co-branded page will be available at{' '}
                    <span style={{ fontWeight: 700, fontFamily: 'monospace' }}>
                      clearseg.com/r/{success.referralCode}
                    </span>{' '}
                    once approved.
                  </div>
                )}
                {!logoFile && !logoUploaded && (
                  <div style={{ marginTop: 16 }}>
                    <p style={{ fontSize: 13, color: C.muted, textAlign: 'center', marginBottom: 10 }}>
                      Want a co-branded landing page? Upload your firm's logo:
                    </p>
                    <div style={{
                      border: `1.5px dashed ${C.border}`,
                      borderRadius: 8,
                      padding: '16px 14px',
                      textAlign: 'center',
                      cursor: 'pointer',
                    }}
                      onClick={() => document.getElementById('logo-input-post').click()}
                    >
                      <input
                        id="logo-input-post"
                        type="file"
                        accept=".png,.jpg,.jpeg,.svg,.pdf,.webp"
                        style={{ display: 'none' }}
                        onChange={async (e) => {
                          if (!e.target.files[0]) return;
                          setLogoUploading(true);
                          try {
                            const fd = new FormData();
                            fd.append('logo', e.target.files[0]);
                            await api.post(`/affiliates/upload-logo/${success.id}`, fd, {
                              headers: { 'Content-Type': 'multipart/form-data' },
                            });
                            setLogoUploaded(true);
                          } catch {
                          } finally {
                            setLogoUploading(false);
                          }
                        }}
                      />
                      <div style={{ fontSize: 13, color: C.muted }}>
                        Click to upload PNG, JPG, SVG, or PDF (max 5MB)
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <form onSubmit={handleSubmit}>
                <h3 style={{ fontSize: isMobile ? 19 : 22, fontWeight: 700, color: C.navy, margin: '0 0 8px' }}>
                  Get your partner page
                </h3>
                <p style={{ fontSize: 14, color: C.muted, margin: '0 0 28px' }}>
                  Takes 2 minutes. You'll get your own referral link and co-branded landing page.
                </p>

                <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 14, marginBottom: 18 }}>
                  <div>
                    <label style={labelStyle}>First Name *</label>
                    <input style={inputStyle} value={form.firstName} onChange={s('firstName')} placeholder="Jane" required
                      onFocus={(e) => (e.target.style.borderColor = C.accent)} onBlur={(e) => (e.target.style.borderColor = C.border)} />
                  </div>
                  <div>
                    <label style={labelStyle}>Last Name *</label>
                    <input style={inputStyle} value={form.lastName} onChange={s('lastName')} placeholder="Smith" required
                      onFocus={(e) => (e.target.style.borderColor = C.accent)} onBlur={(e) => (e.target.style.borderColor = C.border)} />
                  </div>
                </div>

                <div style={{ marginBottom: 18 }}>
                  <label style={labelStyle}>Email *</label>
                  <input style={inputStyle} type="email" value={form.email} onChange={s('email')} placeholder="jane@firm.com" required
                    onFocus={(e) => (e.target.style.borderColor = C.accent)} onBlur={(e) => (e.target.style.borderColor = C.border)} />
                </div>

                <div style={{ marginBottom: 18 }}>
                  <label style={labelStyle}>Phone *</label>
                  <input style={inputStyle} type="tel" value={form.phone}
                    onChange={(e) => setForm(p => ({ ...p, phone: formatPhone(e.target.value) }))}
                    placeholder="(555) 123-4567" required
                    onFocus={(e) => (e.target.style.borderColor = C.accent)} onBlur={(e) => (e.target.style.borderColor = C.border)} />
                </div>

                <div style={{ marginBottom: 18 }}>
                  <label style={labelStyle}>Firm Name *</label>
                  <input style={inputStyle} value={form.firmName} onChange={s('firmName')} placeholder="Smith & Associates CPA" required
                    onFocus={(e) => (e.target.style.borderColor = C.accent)} onBlur={(e) => (e.target.style.borderColor = C.border)} />
                </div>

                <div style={{ marginBottom: 18 }}>
                  <label style={labelStyle}>Profession *</label>
                  <select style={{ ...inputStyle, cursor: 'pointer', appearance: 'auto' }}
                    value={form.licenseType} onChange={s('licenseType')} required
                    onFocus={(e) => (e.target.style.borderColor = C.accent)} onBlur={(e) => (e.target.style.borderColor = C.border)}>
                    {LICENSE_TYPES.map((o) => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </select>
                </div>

                <div style={{ marginBottom: 28 }}>
                  <label style={labelStyle}>License Number (optional)</label>
                  <input style={inputStyle} value={form.licenseNumber} onChange={s('licenseNumber')} placeholder="Optional"
                    onFocus={(e) => (e.target.style.borderColor = C.accent)} onBlur={(e) => (e.target.style.borderColor = C.border)} />
                </div>

                <div style={{ marginBottom: 28 }}>
                  <label style={labelStyle}>Upload Your Firm's Logo (optional)</label>
                  <div style={{
                    border: `1.5px dashed ${logoFile ? C.green : C.border}`,
                    borderRadius: 8, padding: '16px 14px', textAlign: 'center',
                    background: logoFile ? '#F0FDF4' : 'transparent',
                    cursor: 'pointer', transition: 'border-color 0.2s',
                  }}
                    onClick={() => document.getElementById('logo-input').click()}
                  >
                    <input id="logo-input" type="file" accept=".png,.jpg,.jpeg,.svg,.pdf,.webp" style={{ display: 'none' }}
                      onChange={(e) => { if (e.target.files[0]) setLogoFile(e.target.files[0]); }} />
                    {logoFile ? (
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
                        <span style={{ color: C.green, fontSize: 14, fontWeight: 600 }}>&#10003; {logoFile.name}</span>
                        <button type="button" onClick={(e) => { e.stopPropagation(); setLogoFile(null); }}
                          style={{ background: 'none', border: 'none', color: C.muted, cursor: 'pointer', fontSize: 13 }}>Remove</button>
                      </div>
                    ) : (
                      <div>
                        <div style={{ fontSize: 13, color: C.muted, marginBottom: 4 }}>Click to upload PNG, JPG, SVG, or PDF</div>
                        <div style={{ fontSize: 11, color: C.light }}>Max 5MB</div>
                      </div>
                    )}
                  </div>
                  <p style={{ fontSize: 11, color: C.muted, margin: '6px 0 0' }}>
                    Your logo will appear on your co-branded referral landing page.
                  </p>
                </div>

                {error && (
                  <div style={{
                    background: '#FEF2F2', border: '1px solid #FECACA',
                    borderRadius: 8, padding: '10px 14px', marginBottom: 18,
                    fontSize: 13, color: '#DC2626',
                  }}>
                    {error}
                  </div>
                )}

                <button type="submit" disabled={submitting} style={{
                  width: '100%', padding: '14px 24px', borderRadius: 10,
                  border: 'none', background: C.navy, color: '#FFFFFF',
                  fontSize: 15, fontWeight: 700, cursor: submitting ? 'default' : 'pointer',
                  opacity: submitting ? 0.7 : 1,
                  transition: 'opacity 0.15s',
                }}
                onMouseEnter={(e) => { if (!submitting) e.target.style.opacity = '0.9'; }}
                onMouseLeave={(e) => { e.target.style.opacity = submitting ? '0.7' : '1'; }}
                >
                  {submitting ? 'Setting up...' : 'Get My Partner Page'}
                </button>
              </form>
            )}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
